import { BadgeCheck, Building2, FileSearch, QrCode } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

/**
 * The page every sign-in step uses (M31): on large screens, what the
 * service does beside the form; on small ones, the form alone.
 */
export function AuthLayout({
  title,
  intro,
  children,
}: {
  title: string;
  intro?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const points = [
    { icon: FileSearch, text: t("authLayout.check") },
    { icon: BadgeCheck, text: t("authLayout.decide") },
    { icon: QrCode, text: t("authLayout.verify") },
  ];
  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-[#1e1b4b] p-10 text-white lg:flex">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 -top-24 size-96 rounded-full bg-[#4f46e5] opacity-40 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-32 -left-20 size-96 rounded-full bg-[#0891b2] opacity-25 blur-3xl"
        />
        <div className="relative flex items-center gap-3">
          <span className="flex size-9 items-center justify-center rounded-md bg-white/15">
            <Building2 aria-hidden="true" className="size-5" />
          </span>
          <span className="flex flex-col">
            <span className="text-lg font-semibold leading-tight">{t("app.name")}</span>
            <span className="text-xs text-white/70">{t("app.maker")}</span>
          </span>
        </div>
        <div className="relative flex max-w-md flex-col gap-6">
          <p className="text-3xl font-semibold leading-tight tracking-tight">
            {t("authLayout.headline")}
          </p>
          <ul className="flex flex-col gap-4">
            {points.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-3 text-sm text-white/85">
                <Icon aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-[#a5b4fc]" />
                {text}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-white/60">{t("authLayout.footer")}</p>
      </aside>
      <div className="flex flex-col items-center justify-center px-4 py-12 sm:px-8">
        <p className="mb-8 flex items-center gap-2 lg:hidden">
          <span className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Building2 aria-hidden="true" className="size-4" />
          </span>
          <span className="flex flex-col">
            <span className="text-lg font-semibold leading-tight">{t("app.name")}</span>
            <span className="text-xs text-muted-foreground">{t("app.maker")}</span>
          </span>
        </p>
        <main id="main" className="w-full max-w-sm">
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {intro ? <p className="mt-2 text-sm text-muted-foreground">{intro}</p> : null}
          <div className="mt-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
